import React, { useState } from "react";
import { applyGiftCardCodes, removeGiftCardCodes, validateGiftCardCode } from "@src/utils/cartCodeUtils";

interface MinicartGiftCardProps {
  settings?: {
    show_gift_card_section?: boolean;
    gift_card_label?: string;
    gift_card_placeholder?: string;
    gift_card_button_text?: string;
    gift_card_toggle_text?: string;
    gift_card_remove_text?: string;
    background_color?: string;
    border_color?: string;
    border_radius?: string;
    padding?: number;
    margin_bottom?: number;
  };
  appliedGiftCards?: Array<{
    code?: string;
    id?: string;
    lastCharacters?: string;
    balance?: { amount: string; currencyCode: string; } | number;
    amountUsed?: { amount: string; currencyCode: string; } | number;
  }>;
  onGiftCardUpdate?: () => void;
}

export const MinicartGiftCard = ({ 
  settings = {}, 
  appliedGiftCards = [],
  onGiftCardUpdate 
}: MinicartGiftCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const {
    show_gift_card_section = true,
    gift_card_label = "Gift Card",
    gift_card_placeholder = "Enter gift card code",
    gift_card_button_text = "Apply",
    gift_card_toggle_text = "Have a gift card?",
    gift_card_remove_text = "Remove",
    background_color = "#f0fdf4",
    border_color = "#bbf7d0",
    border_radius = "8px",
    padding = 16,
    margin_bottom = 16,
  } = settings;

  if (!show_gift_card_section) return null;

  const containerStyle = {
    backgroundColor: background_color,
    border: `1px solid ${border_color}`,
    borderRadius: border_radius,
    padding: `${Math.max(padding - 4, 8)}px`, // Reduce padding
    marginBottom: `${Math.max(margin_bottom - 4, 8)}px`, // Reduce margin
  };

  const formatMoney = (amount: number | { amount: string; currencyCode: string; }) => {
    if (typeof amount === 'number') {
      const dollars = amount / 100;
      return dollars.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    } else {
      // GraphQL format
      return parseFloat(amount.amount).toLocaleString('en-US', {
        style: 'currency',
        currency: amount.currencyCode,
      });
    }
  };

  const getGiftCardCode = (giftCard: any): string => {
    return giftCard.code || giftCard.lastCharacters || '';
  };

  const getGiftCardDisplayCode = (giftCard: any): string => {
    if (giftCard.lastCharacters) {
      return `****${giftCard.lastCharacters}`;
    } else if (giftCard.code) {
      return `****${giftCard.code.slice(-4)}`;
    }
    return 'Gift Card';
  };

  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    
    setIsLoading(true);
    setError("");

    try {
      // Validate gift card code first
      await validateGiftCardCode(giftCardCode);

      // Trigger haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }

      // Apply gift card code using utility
      const existingCodes = appliedGiftCards.map(getGiftCardCode).filter(Boolean);
      const updatedCodes = [...existingCodes, giftCardCode.trim()];
      await applyGiftCardCodes(updatedCodes);

      // Success feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([100, 50, 100]);
      }

      setGiftCardCode("");
      setIsExpanded(false);
      
      // Notify parent to refresh cart
      if (onGiftCardUpdate) {
        onGiftCardUpdate();
      }

      // Dispatch cart update event
      window.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { action: 'gift_card_applied' }
      }));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply gift card');
      
      // Error haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([50, 50, 50]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveGiftCard = async (codeToRemove: string) => {
    setIsLoading(true);
    setError("");

    try {
      // Trigger haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate(30);
      }

      // Remove gift card code using utility
      await removeGiftCardCodes([codeToRemove]);

      // Notify parent to refresh cart
      if (onGiftCardUpdate) {
        onGiftCardUpdate();
      }

      // Dispatch cart update event
      window.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { action: 'gift_card_removed' }
      }));

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove gift card');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="minicart-gift-card animate-slideInUp" style={containerStyle}>
      {/* Applied Gift Cards */}
      {appliedGiftCards.length > 0 && (
        <div className="applied-gift-cards mb-2">
          {appliedGiftCards.map((giftCard, index) => (
            <div 
              key={index}
              className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 mb-1 animate-bounceIn"
            >
              <div className="flex flex-col">
                <div className="flex items-center">
                  <span className="text-emerald-700 font-medium text-xs">
                    {getGiftCardDisplayCode(giftCard)}
                  </span>
                  <span className="ml-1 text-emerald-600 text-xs">✓</span>
                </div>
                {giftCard.balance && (
                  <div className="text-emerald-600 text-xs">
                    Bal: {formatMoney(giftCard.balance)}
                    {giftCard.amountUsed && (
                      <span className="ml-1">
                        (Used: {formatMoney(giftCard.amountUsed)})
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleRemoveGiftCard(getGiftCardCode(giftCard))}
                disabled={isLoading || !getGiftCardCode(giftCard)}
                className="text-emerald-600 hover:text-emerald-800 text-xs font-medium transition-colors duration-200 disabled:opacity-50 px-1"
              >
                {gift_card_remove_text}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Toggle Button */}
      {!isExpanded && (
        <button
          onClick={() => {
            setIsExpanded(true);
            if ('vibrate' in navigator) {
              navigator.vibrate(30);
            }
          }}
          className="w-full text-left text-emerald-600 hover:text-emerald-800 font-medium text-xs transition-colors duration-200 btn-animate py-1"
        >
          {gift_card_toggle_text}
        </button>
      )}

      {/* Gift Card Input Form */}
      {isExpanded && (
        <div className="gift-card-form animate-slideInUp">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {gift_card_label}
          </label>
          
          <div className="flex gap-1">
            <input
              type="text"
              value={giftCardCode}
              onChange={(e) => {
                setGiftCardCode(e.target.value);
                setError("");
              }}
              placeholder={gift_card_placeholder}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleApplyGiftCard();
                }
              }}
            />
            <button
              onClick={handleApplyGiftCard}
              disabled={isLoading || !giftCardCode.trim()}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium text-xs transition-all duration-200 btn-animate"
            >
              {isLoading ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                gift_card_button_text
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-1 text-red-600 text-xs animate-slideInUp">
              {error}
            </div>
          )}

          {/* Collapse Button */}
          <button
            onClick={() => {
              setIsExpanded(false);
              setGiftCardCode("");
              setError("");
              if ('vibrate' in navigator) {
                navigator.vibrate(30);
              }
            }}
            className="mt-2 text-gray-500 hover:text-gray-700 text-xs transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
